import { IconDeposit, IconDoor } from "@assets";
import { useDepositDrawer } from "@components/utils/DepositDrawer/hooks";
import { openOnboardingDialog } from "@components/utils/OnboardingDialog/utils";
import { AuthSource, DepositSource, useStatsigTracking } from "@configs/statsig";
import { OnboardMode } from "@constants";
import useFTDEligibility from "@hooks/useFTDEligibility";
import { UserMode } from "@types";
import { getThemeAsset } from "@utils/theme";
import { cn } from "kz-ui-sdk";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FTDGiftTextButton, LOOP_CONFIG } from "../FTDGiftText";
import { GiftTextButton } from "../RegisterGiftText";

interface CenterButtonProps {
  userMode: UserMode;
}

const CenterButton = ({ userMode }: CenterButtonProps) => {
  const { t } = useTranslation();
  const { trackRegisterClick } = useStatsigTracking();
  const { openDepositDrawer } = useDepositDrawer();

  const isFTDEligible = useFTDEligibility();

  const [isGiftVisible, setIsGiftVisible] = useState(false);
  const startTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (startTimerRef.current) clearTimeout(startTimerRef.current);
    };
  }, []);

  // Show gift text when FTD eligible
  useEffect(() => {
    if (isFTDEligible) {
      if (startTimerRef.current) clearTimeout(startTimerRef.current);
      startTimerRef.current = setTimeout(() => {
        setIsGiftVisible(true);
      }, LOOP_CONFIG.INITIAL_DELAY);
    } else {
      setIsGiftVisible(false);
    }
  }, [isFTDEligible]);

  const handleCenterButtonClick = () => {
    if (userMode === UserMode.MEMBER) {
      // Track deposit click from bottom navigation (tracking happens automatically in openDepositDrawer)
      openDepositDrawer({ source: DepositSource.BOTTOM_NAVIGATION });
    } else {
      // Track registration click from bottom navigation
      trackRegisterClick(AuthSource.BOTTOM_NAVIGATION_CENTER_BUTTON);
      openOnboardingDialog({
        mode: OnboardMode.REGISTER,
      });
    }
  };

  const mainButtonLabel = `Bottom navigation ${userMode === UserMode.MEMBER ? "Deposit" : "Register"}`;

  return (
    <div className="relative inline-block">
      {userMode === UserMode.MEMBER && isFTDEligible && isGiftVisible && <FTDGiftTextButton />}
      {userMode === UserMode.GUEST && <GiftTextButton />}
      <button
        onClick={handleCenterButtonClick}
        className="group inline-block h-[60px] w-[60px] translate-y-[-10px]"
        aria-label={mainButtonLabel}
        data-element-id="button-bottom-navigation-center"
      >
        <div
          className={cn(
            "relative h-full w-full rounded-full bg-bottomNavBtnRound shadow-[0px_4px_6px_0px_rgba(0,_0,_0,_0.24)] transition-transform will-change-transform active:translate-y-0.5",
          )}
        >
          {/*COLOR MIST BG*/}
          <div
            className="absolute start-0 top-0 h-full w-full animate-spin rounded-full will-change-transform [animation-duration:16s]"
            style={{
              backgroundImage: `url(${getThemeAsset("misc/bg-nav-center.png")})`,
            }}
          />

          {/*ICON & TEXT*/}
          <div className="absolute start-0 top-0 flex h-full w-full flex-col items-center justify-center">
            {/*DEPOSIT*/}
            {userMode === UserMode.MEMBER && (
              <>
                <IconDeposit
                  width={20}
                  height={20}
                  className="text-content-base"
                  aria-label={mainButtonLabel}
                />
                <span
                  className="text-xs font-bold text-content-base"
                  aria-label={mainButtonLabel}
                >
                  {t("Deposit")}
                </span>
              </>
            )}
            {/*REGISTER*/}
            {userMode === UserMode.GUEST && (
              <>
                <IconDoor
                  width={20}
                  height={20}
                  className="text-content-base"
                  aria-label={mainButtonLabel}
                />
                <span
                  className="text-xs font-bold text-content-base"
                  aria-label={mainButtonLabel}
                >
                  {t("nav register")}
                </span>
              </>
            )}
          </div>

          {/*RIM LIGHT*/}
          <div
            className={`absolute start-0 top-0 h-full w-full rounded-full border-0 border-dashed border-[rgba(0,0,0,0.20)] bg-bottomNavBtnRoundRimLight shadow-[inset_0px_2px_1px_0px_rgba(255,255,255,0.24),_inset_0px_4px_4px_0px_rgba(255,255,255,0.25)]`}
            aria-label={mainButtonLabel}
          />
          {/*GRAY OVERLAY*/}
          <div
            className={`absolute start-0 top-0 h-full w-full rounded-full bg-[rgba(217,217,217,0.01)]`}
            aria-label={mainButtonLabel}
          />
        </div>
      </button>
    </div>
  );
};

export default CenterButton;
